<script setup>
/**
 * Conexão por código ("Conectar com número de telefone" no celular), alternativa ao QR.
 * Só o gateway WhatsApp Cortex ZNew tem. O código novo chega sozinho pelo evento
 * baileys.connection.pairing_code_generated; o código em si vem só pela API.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import { toast } from 'dashboard/helper/toast';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
  phoneNumber: { type: String, default: '' },
});

const emit = defineEmits(['useQr', 'status']);
const { t } = useI18n();

// O gateway aceita 3 códigos novos a cada 10 min (proteção contra restrição do número).
// simplificação: a conta é desta tela (outra aba ou a criação da caixa não entram); quem decide é o gateway,
// que responde 429 com a mensagem mostrada no erro. Se precisar exato, o gateway devolve as vagas no GET.
const MAX_RENEWALS = 3;
const RENEW_WINDOW_MS = 10 * 60 * 1000;

const pairing = ref(null); // { code, expiresAt }
const renewedAt = ref([]); // horários dos códigos novos pedidos nesta tela
const isLoading = ref(true); // já abre buscando: nada de "ainda não saiu" antes da 1ª resposta
const error = ref('');
const now = ref(Date.now());
let clock = null;
let lastRequest = 0; // só a resposta do pedido mais recente vale
let refetchAfter = false; // código novo avisado enquanto um pedido estava em andamento

const secondsLeft = computed(() => {
  if (!pairing.value?.expiresAt) return 0;
  const ms = Date.parse(pairing.value.expiresAt) - now.value;
  return Math.max(0, Math.floor(ms / 1000));
});
const countdown = computed(() => {
  const s = secondsLeft.value;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
});
const formattedCode = computed(() => {
  const code = pairing.value?.code || '';
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
});
// Oito casas sempre na tela (vazias enquanto o código não chega): o layout não pula quando ele aparece.
const codeGroups = computed(() => {
  const chars = (pairing.value?.code || '').padEnd(8).slice(0, 8).split('');
  return [chars.slice(0, 4), chars.slice(4)];
});
const isExpired = computed(() => Boolean(pairing.value) && !secondsLeft.value);
const steps = computed(() => [
  t('INBOX_MGMT.BAILEYS_PAIRING.STEP_1'),
  t('INBOX_MGMT.BAILEYS_PAIRING.STEP_2'),
  t('INBOX_MGMT.BAILEYS_PAIRING.STEP_3'),
  t('INBOX_MGMT.BAILEYS_PAIRING.STEP_4'),
]);
const recentRenewals = computed(() =>
  renewedAt.value.filter(at => now.value - at < RENEW_WINDOW_MS)
);
const codesLeft = computed(() =>
  Math.max(0, MAX_RENEWALS - recentRenewals.value.length)
);
// Quando o mais antigo dos códigos da janela sai dela, libera mais um.
const nextFreeIn = computed(() => {
  const s = Math.max(
    0,
    Math.ceil((recentRenewals.value[0] + RENEW_WINDOW_MS - now.value) / 1000)
  );
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
});

// renew: código novo (o gateway abre outra conexão de pareamento); sem renew, o código atual.
// Um pedido por vez: os botões ficam desligados enquanto carrega, e resposta de pedido ultrapassado é
// descartada (senão um código antigo, de sessão já substituída, cobria o novo).
const fetchCode = async (renew = false) => {
  if (renew && isLoading.value) return;
  lastRequest += 1;
  const request = lastRequest;
  isLoading.value = true;
  error.value = '';
  if (renew) {
    pairing.value = null;
    renewedAt.value = [...recentRenewals.value, Date.now()];
  }
  try {
    const { data } = await InboxesAPI.baileysGetPairingCode(
      props.inboxId,
      renew
    );
    if (request !== lastRequest) return;
    pairing.value = data?.pairing_code || null;
    if (data?.status) emit('status', data.status);
  } catch (e) {
    if (request !== lastRequest) return;
    error.value =
      e?.response?.data?.error || t('INBOX_MGMT.BAILEYS_PAIRING.ERROR');
  } finally {
    if (request === lastRequest) {
      isLoading.value = false;
      // O aviso de código pronto chegou no meio do pedido: busca de novo agora, em vez de perder o aviso.
      if (refetchAfter) {
        refetchAfter = false;
        fetchCode();
      }
    }
  }
};

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(pairing.value?.code || '');
    toast.success(t('INBOX_MGMT.BAILEYS_PAIRING.COPIED'));
  } catch {
    toast.error(t('INBOX_MGMT.BAILEYS_PAIRING.COPY_FAILED'));
  }
};

const onCodeGenerated = data => {
  if (Number(data.inbox_id) !== props.inboxId) return;
  if (isLoading.value) refetchAfter = true;
  else fetchCode();
};

onMounted(() => {
  fetchCode();
  emitter.on(BUS_EVENTS.BAILEYS_PAIRING_CODE_GENERATED, onCodeGenerated);
  clock = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.BAILEYS_PAIRING_CODE_GENERATED, onCodeGenerated);
  clearInterval(clock);
});
</script>

<template>
  <div class="max-w-md mx-auto text-left">
    <h2 class="text-lg font-semibold text-n-slate-12">
      {{ t('INBOX_MGMT.BAILEYS_PAIRING.TITLE') }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.BAILEYS_PAIRING.SUBTITLE', { phone: phoneNumber }) }}
    </p>

    <p
      v-if="error"
      role="alert"
      class="mt-4 mb-0 flex items-start gap-2 p-3 rounded-lg bg-n-ruby-2 outline outline-1 outline-n-ruby-6 text-sm text-n-ruby-11"
    >
      <span
        class="i-lucide-circle-alert size-4 mt-0.5 flex-shrink-0"
        aria-hidden="true"
      />
      {{ error }}
    </p>

    <div class="mt-6 flex items-center gap-3">
      <div
        class="flex items-center gap-2"
        :class="{ 'opacity-40': isExpired }"
        :aria-busy="isLoading"
        :aria-label="
          pairing ? formattedCode : t('INBOX_MGMT.BAILEYS_PAIRING.LOADING')
        "
      >
        <template v-for="(group, g) in codeGroups" :key="g">
          <span v-if="g" class="w-2 h-px bg-n-slate-8" aria-hidden="true" />
          <span class="flex gap-1.5" aria-hidden="true">
            <span
              v-for="(char, c) in group"
              :key="c"
              class="w-9 h-11 flex items-center justify-center rounded-lg font-mono text-xl font-medium text-n-slate-12"
              :class="
                char.trim()
                  ? 'bg-n-alpha-1 outline outline-1 outline-n-weak'
                  : 'bg-n-alpha-2'
              "
            >
              {{ char }}
            </span>
          </span>
        </template>
      </div>
      <NextButton
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-copy"
        :disabled="!pairing || isExpired"
        :label="t('INBOX_MGMT.BAILEYS_PAIRING.COPY')"
        @click="copyCode"
      />
    </div>

    <p
      class="mt-3 mb-0 h-5 flex items-center gap-2 text-sm"
      :class="isExpired ? 'text-n-amber-11' : 'text-n-slate-11'"
      aria-live="polite"
    >
      <template v-if="isLoading">
        <Spinner :size="14" />
        {{ t('INBOX_MGMT.BAILEYS_PAIRING.LOADING') }}
      </template>
      <template v-else-if="pairing && !isExpired">
        <span class="i-lucide-clock size-3.5" aria-hidden="true" />
        {{ t('INBOX_MGMT.BAILEYS_PAIRING.EXPIRES_IN', { time: countdown }) }}
      </template>
      <template v-else-if="isExpired">
        <span class="i-lucide-triangle-alert size-3.5" aria-hidden="true" />
        {{ t('INBOX_MGMT.BAILEYS_PAIRING.EXPIRED') }}
      </template>
      <template v-else>{{ t('INBOX_MGMT.BAILEYS_PAIRING.NOT_YET') }}</template>
    </p>

    <ol
      class="mt-6 mb-0 flex flex-col gap-2 text-sm text-n-slate-11 list-none p-0"
    >
      <li v-for="(step, n) in steps" :key="step" class="flex items-start gap-3">
        <span
          class="size-5 flex-shrink-0 flex items-center justify-center rounded-full bg-n-alpha-2 text-xs font-medium text-n-slate-12 tabular-nums"
          aria-hidden="true"
        >
          {{ n + 1 }}
        </span>
        {{ step }}
      </li>
    </ol>

    <div
      class="mt-6 pt-4 border-t border-n-weak flex items-center justify-end gap-4 flex-wrap"
    >
      <div class="flex items-center gap-2">
        <NextButton
          type="button"
          variant="link"
          color="slate"
          size="sm"
          :disabled="isLoading"
          :label="t('INBOX_MGMT.BAILEYS_PAIRING.USE_QR')"
          @click="emit('useQr')"
        />
        <NextButton
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          :disabled="codesLeft === 0 || isLoading"
          :label="
            codesLeft === 0
              ? t('INBOX_MGMT.BAILEYS_PAIRING.LIMIT', { time: nextFreeIn })
              : t('INBOX_MGMT.BAILEYS_PAIRING.GENERATE', { count: codesLeft })
          "
          @click="fetchCode(true)"
        />
      </div>
    </div>
  </div>
</template>
